import type { Label, LabelColor } from '@/lib/api/types';
import { cn } from '@/lib/utils';

/**
 * Colour classes per label colour.
 *
 * Spelled out rather than composed as `bg-label-${color}-bg`, because Tailwind
 * scans source text for class names — an interpolated one is never generated,
 * and the chip silently renders unstyled.
 */
const STYLES: Record<LabelColor, string> = {
  slate: 'bg-label-slate-bg text-label-slate',
  red: 'bg-label-red-bg text-label-red',
  amber: 'bg-label-amber-bg text-label-amber',
  green: 'bg-label-green-bg text-label-green',
  teal: 'bg-label-teal-bg text-label-teal',
  blue: 'bg-label-blue-bg text-label-blue',
  violet: 'bg-label-violet-bg text-label-violet',
  pink: 'bg-label-pink-bg text-label-pink',
};

const DOT: Record<LabelColor, string> = {
  slate: 'bg-label-slate',
  red: 'bg-label-red',
  amber: 'bg-label-amber',
  green: 'bg-label-green',
  teal: 'bg-label-teal',
  blue: 'bg-label-blue',
  violet: 'bg-label-violet',
  pink: 'bg-label-pink',
};

export function LabelChip({
  label,
  size = 'sm',
  className,
}: {
  label: Pick<Label, 'name' | 'color'>;
  size?: 'sm' | 'md';
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex max-w-[10rem] items-center rounded font-medium',
        size === 'sm' ? 'px-1.5 py-0.5 text-[11px]' : 'px-2 py-0.5 text-xs',
        STYLES[label.color],
        className,
      )}
      title={label.name}
    >
      <span className="truncate">{label.name}</span>
    </span>
  );
}

/** Just the colour, for pickers and the sidebar where the name is beside it. */
export function LabelDot({
  color,
  className,
}: {
  color: LabelColor;
  className?: string;
}) {
  return (
    <span
      className={cn('h-2 w-2 shrink-0 rounded-full', DOT[color], className)}
      aria-hidden
    />
  );
}
